/**
 * MomentPublicUploadPage — /m/:slug — public, no auth.
 *
 * This is what guests land on after scanning the QR. They see the event
 * branding, then the native camera / gallery uploader, and the live
 * gallery of approved shots.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Loader2, Tv, MapPin, CalendarDays } from "lucide-react";
import MomentsMediaUploader from "@/components/MomentsMediaUploader";
import MomentsGallery from "@/components/MomentsGallery";

const API = process.env.REACT_APP_BACKEND_URL ||
             (typeof window !== "undefined" ? window.location.origin : "");

export default function MomentPublicUploadPage() {
  const { slug } = useParams();
  const [page,    setPage]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/moments/m/${slug}`);
        const j = await r.json();
        if (!alive) return;
        if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
        setPage(j);
        // Set the tab title dynamically for a nice UX
        if (typeof document !== "undefined") {
          document.title = `${j.title} · Moments`;
        }
      } catch (e) {
        if (!alive) return;
        setError(e.message || String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="animate-spin text-stone-400" size={22}/>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50 text-stone-800 p-6"
           data-testid="moments-public-error">
        <Camera className="text-stone-300 mb-4" size={40}/>
        <h1 className="text-2xl font-black">Page not available</h1>
        <p className="text-stone-500 mt-2 text-center max-w-md">
          This Moment Page is paused, deleted, or the link is wrong.
          Please check the QR you scanned.
        </p>
      </div>
    );
  }

  const accent = page.accent_hex || "#f59e0b";

  return (
    <div className="min-h-screen bg-stone-50 pb-16" data-testid="moments-public-root">
      {/* Branded hero */}
      <section
        className="relative"
        style={{
          background: page.cover_url
            ? `linear-gradient(180deg, rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${page.cover_url})`
            : `linear-gradient(180deg, ${accent}, ${accent}bb)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-2xl mx-auto px-5 pt-14 pb-10 text-white">
          <p className="text-xs uppercase font-bold tracking-widest opacity-80">
            {page.theme}
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-black drop-shadow-lg"
              data-testid="moments-public-title">
            {page.title}
          </h1>
          {page.description && (
            <p className="mt-3 text-sm sm:text-base text-white/95 drop-shadow"
               data-testid="moments-public-description">
              {page.description}
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-4 text-xs text-white/90">
            {page.location && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={12}/> {page.location}
              </span>
            )}
            {page.event_date && (
              <span className="inline-flex items-center gap-1">
                <CalendarDays size={12}/> {page.event_date}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Camera size={12}/> {page.media_approved} moments so far
            </span>
          </div>
        </div>
      </section>

      {/* Upload card */}
      <section className="max-w-2xl mx-auto px-5 -mt-6">
        <div className="rounded-3xl bg-white shadow-xl shadow-stone-900/10 p-5 sm:p-6">
          <h2 className="text-lg font-black text-stone-900 mb-1">
            Share your photos & videos
          </h2>
          <p className="text-xs text-stone-500 mb-4">
            No app needed. Nothing signs you up. Every upload lands
            directly in the host&apos;s gallery.
          </p>
          <MomentsMediaUploader
            slug={slug}
            accentHex={accent}
            allowVideo={page.allow_video}
            allowCaption={page.allow_captions}
            guestUrl={page.guest_url}
            onUploaded={() => setRefresh((v) => v + 1)}
          />
        </div>
      </section>

      {/* Live gallery */}
      <section className="max-w-3xl mx-auto px-5 mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-black text-stone-900">
            What everyone shared
          </h2>
          <Link
            to={`/m/${slug}/live`}
            data-testid="moments-public-live-link"
            className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900"
          >
            <Tv size={13}/> TV slideshow
          </Link>
        </div>
        <MomentsGallery
          slug={slug}
          isOwner={false}
          filter="approved"
          refreshKey={refresh}
          testid="moments-public-gallery"
        />
      </section>

      <footer className="mt-16 text-center text-xs text-stone-400">
        Powered by <a href="/moments" className="underline underline-offset-2 hover:text-stone-700">HustleSuite Moments</a>
      </footer>
    </div>
  );
}
